'use client';

import { useEffect } from 'react';
import Link from 'next/link';
import { RefreshCw, ArrowLeft } from 'lucide-react';

export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    // Log the error to an error reporting service
    console.error(error);
  }, [error]);

  return (
    <html lang="en">
      <head>
        {/* Add crucial styles inline to ensure proper rendering before CSS is loaded */}
        <style dangerouslySetInnerHTML={{ __html: `
          :root { color-scheme: light dark; }
          body { 
            margin: 0; 
            padding: 0; 
            font-family: system-ui, sans-serif; 
          }
          .center-wrapper {
            display: flex;
            justify-content: center;
            width: 100%;
          }
          @media (prefers-color-scheme: dark) {
            body { 
              color: #fff;
              background: linear-gradient(to bottom, #111827, #000000);
            }
            .dark-shadow {
              box-shadow: 0 25px 50px -12px rgba(31, 41, 55, 0.25), 
                          0 10px 15px -3px rgba(0, 0, 0, 0.3),
                          0 0 0 1px rgba(255, 255, 255, 0.05);
            }
          }
        `}} />
      </head>
      <body className="bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-black">
        <div className="center-wrapper">
          <div className="w-full max-w-[1440px]">
            <div className="min-h-screen flex items-center justify-center relative p-4">
              {/* Fixed background */}
              <div className="absolute inset-0 bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-black"></div>
              
              {/* Content container with fixed positioning */}
              <div className="w-full max-w-xl mx-auto px-6 py-10 sm:px-10 sm:py-14 premium-glass-nav dark:premium-glass-dark-nav premium-radius-xl premium-shadow-lg backdrop-blur-md text-center relative z-10 dark-shadow">
                {/* Error icon */}
                <div className="mb-8">
                  <div className="flex justify-center mb-6">
                    <svg 
                      className="h-24 w-24 text-red-500 dark:text-red-400 drop-shadow-sm dark:drop-shadow-lg"
                      xmlns="http://www.w3.org/2000/svg" 
                      fill="none" 
                      viewBox="0 0 24 24" 
                      stroke="currentColor"
                    >
                      <path 
                        strokeLinecap="round" 
                        strokeLinejoin="round" 
                        strokeWidth={1.5} 
                        d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" 
                      />
                    </svg>
                  </div>
                  
                  <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 dark:text-white mb-4">Critical Error</h1>
                  
                  <p className="text-gray-600 dark:text-gray-300 mb-8 text-base sm:text-lg max-w-md mx-auto">
                    We're sorry, but a critical error has occurred that prevents the application from functioning.
                    {error.digest && (
                      <span className="block mt-2 text-sm text-gray-500 dark:text-gray-400">
                        Error reference: {error.digest}
                      </span>
                    )}
                  </p>
                </div>

                <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                  <button 
                    onClick={() => reset()}
                    className="inline-flex items-center justify-center w-full sm:w-auto h-12 px-6 text-base font-medium text-white premium-gradient-primary premium-radius-button premium-shadow-md premium-transition-all premium-hover-lift transform-gpu dark:shadow-lg dark:shadow-primary-900/40"
                  >
                    <RefreshCw size={18} className="mr-2" />
                    Try Again
                  </button>
                  
                  <Link href="/" className="w-full sm:w-auto">
                    <button className="inline-flex items-center justify-center w-full sm:w-auto h-12 px-6 text-base font-medium text-primary-600 dark:text-gray-100 bg-white dark:bg-gray-800 border border-primary-200 dark:border-gray-700 hover:border-primary-300 dark:hover:border-gray-600 premium-radius-button premium-shadow-sm premium-transition-all premium-hover-lift transform-gpu dark:shadow-lg dark:shadow-gray-900/40">
                      <ArrowLeft size={18} className="mr-2" />
                      Return Home
                    </button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </body>
    </html>
  );
} 